import axios from "axios";
import { includes, pathOr } from "ramda";
import { logout } from "./utils";

axios.defaults.headers.common["Accept"] = "application/json";
axios.defaults.headers.common["Content-Type"] = "application/json";
axios.defaults.withCredentials = true;

let isRefreshTokenInProgress = false;

const getRefreshToken = async () => {
  const doLogout = () => {
    localStorage.clear();
    location.reload();
  };

  if (isRefreshTokenInProgress) return;

  try {
    isRefreshTokenInProgress = true;
    const url = import.meta.env.VUE_APP_API_URL + "v1/refresh-cookie-token";
    await axios.post(url);
  } catch (error) {
    const statusCode = pathOr(401, ["response", "status"], error);
    if (includes(statusCode, [401, 412])) {
      doLogout();
    }
    throw error;
  } finally {
    isRefreshTokenInProgress = false;
  }
};

const generateHeaders = async (extraHeaders: any = {}) => {
  axios.defaults.withCredentials = true;

  const config: any = {
    headers: { Accept: "application/json" },
  };
  for (const [key, value] of Object.entries(extraHeaders)) {
    config.headers[key] = value;
  }
  return config;
};

const fillHeaders = (extraHeaders: any = {}) => {
  const headers: { [key: string]: any } = {};

  if (extraHeaders) {
    for (const [key, value] of Object.entries(extraHeaders)) {
      headers[key] = value;
    }
  }

  return headers;
};

const get = async (
  url: string,
  params: any = false,
  headers: any = {},
  token = true
) => {
  if (token) {
    headers = await generateHeaders(headers);
    if (!headers) logout();
  }
  if (params) {
    headers.params = params;
  }

  return response(axios.get(url, headers));
};

const post = async (
  url: string,
  payload: any = {},
  headers = {},
  token = true,
  config = {}
) => {
  if (token) {
    headers = await generateHeaders(headers);
    if (!headers) return false;
  }
  return response(
    axios.post(url, payload, {
      ...headers,
      ...config,
    })
  );
};

const put = async (
  url: string,
  payload: any = {},
  headers = {},
  token = true
) => {
  if (token) {
    headers = await generateHeaders(headers);
    if (!headers) return false;
  }
  return response(axios.put(url, payload, headers));
};

const patch = (url: string, payload: any = {}, headers = {}, token = true) => {
  if (token) {
    headers = fillHeaders(headers);
    if (!headers) return false;
  }
  return response(axios.patch(url, payload, headers));
};

const all = (url: string[]) => {
  return response(axios.all(url));
};

const remove = async (
  url: string,
  data: any = {},
  headers: any = {},
  token = true
) => {
  if (token) {
    headers = await generateHeaders(headers);
    if (!headers) return false;
  }
  if (data) {
    headers.params = data;
  }
  return response(axios.delete(url, headers));
};

const response: any = async (axiosMethod: any) => {
  try {
    const response = await axiosMethod;
    return response;
  } catch (error: any) {
    const statusCode = pathOr(401, ["response", "status"], error);
    if (statusCode === 401) {
      await getRefreshToken();
      return response(axios(error.config));
    } else {
      throw error;
    }
  }
};

export default { get, post, put, patch, all, remove };
